import { CardLoader } from 'components/organisms'
import withLoader from 'hocs/with-loader'
import PropTypes from 'prop-types'
import { Button, ButtonGroup, Card, CardBody, Input, InputGroup, ListGroup, ListGroupItem, Table, Collapse, Label } from 'reactstrap'
import { useEffect, useState } from 'react'
import StringUtil from 'utils/string-util'
import { DEAL_STATE, PAYMENT_METHOD,  } from 'domains/sales/deals/constants'
import { PAYMENT_STATE } from '../constants'
import TimeUtil from 'utils/time-util'
import TableUtil from 'utils/table-util'

const propTypes = {
  payment: PropTypes.object,
  fetchPayment: PropTypes.func,
  updateDownPayment: PropTypes.func,
  confirmManualTransfer: PropTypes.func,
  rejectManualTransfer: PropTypes.func,
}

const defaultValues = {
  payment: {},
  fetchPayment: () => {},
  updateDownPayment: () => {},
  confirmManualTransfer: () => {},
  rejectManualTransfer: () => {},
}

/**
 * Displaying detail of Payment
 * @param {Object} payment
 */
const FinancePaymentsView = ({
  payment,
  fetchPayment,
  updateDownPayment,
  confirmManualTransfer,
  rejectManualTransfer,
  fetchHistoryList,
  historyList,
  fetchHistoryDetail,
  historyDetail,
  clearHistoryDetail,
}) => {
  const deal = payment.deal
  const [ downPayment, setDownPayment ] = useState(0)
  const [ isHistoryOpen, setIsHistoryOpen ] = useState(false)
  const [ showDifferenceOnly, setShowDifferenceOnly ] = useState(false)

  useEffect(() => {
    if(isHistoryOpen){
      fetchHistoryList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isHistoryOpen])

  useEffect(() => {
    setDownPayment(payment.deal.down_payment)
  }, [payment.deal.down_payment])
  const stillNotFullyPaid = (payment.deal.down_payment ?? 0) > 0
  const fullyPaid = !stillNotFullyPaid

  const tableData = TableUtil.convertHistoryDetailIntoTableData(historyDetail)

  return (
    <>
      <Card>
        <div className='p-3'>
          <div>
            <h2 className='mb-3 text-center'>{ `Summary (#${payment.id}) - ${payment.reference_number} - ${deal.reference_number}` }</h2>
            <Button onClick={fetchPayment} block color="info" className='mb-3'>Refresh Data</Button>
            <Table
              bordered
              responsive
              striped
            >
              <tbody>
                <tr>
                  <th scope="row" className='text-right'>
                    Final Payment
                  </th>
                  <td>
                    {StringUtil.currencyCase(deal.final_payment)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    Status
                  </th>
                  <td>
                    {StringUtil.capitalizeAll(StringUtil.humanize(deal.state))}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    Pembayaran
                  </th>
                  <td>
                    {StringUtil.capitalizeAll(StringUtil.humanize(payment.method_type))}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    User Email
                  </th>
                  <td>
                    {payment.user.email}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    User Full Name
                  </th>
                  <td>
                    {payment.user.detail.name}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    Down Payment
                  </th>
                  <td>
                    {deal.down_payment ? StringUtil.currencyCase(deal.down_payment) : "-"}
                  </td>
                </tr>
              </tbody>
            </Table>
            {
              deal.state === DEAL_STATE.WAITING_FOR_CONFIRMATION
              &&
              <Button onClick={confirmManualTransfer} block color="danger">Cancel</Button>
            }
          </div>
        </div>
      </Card>
      {
        stillNotFullyPaid
        &&
        <Card>
          <CardBody>
            <h2 className='mb-3 text-center'>Ubah Down Payment</h2>
            <InputGroup>
              <Input
                value={downPayment}
                onChange={(e) => setDownPayment(
                  Math.min(parseFloat(deal.final_payment), parseFloat(e.target.value))
                )}
              />
            </InputGroup>
            <p>{StringUtil.currencyCase(downPayment)}</p>
            <ButtonGroup style={{marginLeft: 0, marginTop: "8px", width: "100%"}}>
              <Button
                color="success"
                onClick={() => updateDownPayment(true, downPayment)}
              >
                Konfirmasi Pembayaran Penuh
              </Button>
              <Button
                color="info"
                onClick={() => updateDownPayment(false, downPayment)}
              >
                Simpan
              </Button>
            </ButtonGroup>
          </CardBody>
        </Card>
      }
      {
        [PAYMENT_METHOD.MANUAL_TRANSFER, PAYMENT_METHOD.CASH, PAYMENT_METHOD.EDC].includes(payment.method_type)
        &&
        <Card>
          <CardBody>
            <h2 className='mb-3 text-center'>Manual Transfer</h2>
            {
              fullyPaid && payment.state === PAYMENT_STATE.PENDING
              &&
              <Button
                color="danger"
                style={{marginLeft: 0, marginBottom: "8px", width: "100%"}}
                onClick={() => rejectManualTransfer()}
              >
                Tolak
              </Button>
            }
            <ListGroup className='text-sm'>
              <ListGroupItem>
                Nama Bank: {deal.bank || "-"}
              </ListGroupItem>
              <ListGroupItem>
                Nama Akun Bank: {deal.bank_account_name || "-"}
              </ListGroupItem>
              <ListGroupItem>
                No. Akun Bank: {deal.bank_account_number || "-"}
              </ListGroupItem>
            </ListGroup>
            {
              fullyPaid && payment.state === PAYMENT_STATE.PENDING
              &&
              <Button
                color="success"
                style={{marginLeft: 0, marginTop: "8px", width: "100%"}}
                onClick={() => confirmManualTransfer()}
              >
                Konfirmasi
              </Button>
            }
            {
              payment.transfer_receipt_url &&
              <ListGroupItem>
                <Label>Bukti Transfer</Label>
                {
                  payment.transfer_receipt_url.endsWith(".pdf") ? (
                    <a href={payment.transfer_receipt_url} target="_blank" rel="noopener noreferrer">
                      Buka Bukti Transfer (PDF)
                    </a>
                  ) : (
                    <img
                      src={payment.transfer_receipt_url}
                      alt="Transfer Receipt"
                      style={{ width: "100%", maxHeight: 300, objectFit: "contain" }}
                    />
                  )
                }
              </ListGroupItem>
            }
          </CardBody>
        </Card>
      }
      <Card>
        <CardBody>
          <Button
            color="info"
            style={{marginLeft: 0, marginBottom: "8px", width: "100%"}}
            onClick={() => setIsHistoryOpen(!isHistoryOpen)}
          >
            Lihat History
          </Button>
          <Collapse isOpen={isHistoryOpen}>
            <Card>
              <CardBody>
                <h2 className='mb-3 text-center'>History</h2>

                <ListGroup>
                  {
                    historyList.map((history) => {
                      const isActive = history.id === historyDetail.id
                      return (
                        <>
                          <ListGroupItem
                            action
                            active={isActive}
                            tag="button"
                            onClick={() => {
                              setShowDifferenceOnly(false)
                              if(isActive){
                                clearHistoryDetail()
                              }else{
                                fetchHistoryDetail(history.id)
                              }
                            }}
                          >
                            {TimeUtil.format(history.created_at, 'YYYY-MM-DD HH:mm:ss')}: ({history.done_by}) {history.action_type.replaceAll("_", " ")}
                          </ListGroupItem>
                          {
                            isActive
                            &&
                            <>
                              <Label className='text-gray-light text-nowrap mt-2'>
                                Lihat field yang berubah saja
                              </Label>
                              <Label className="custom-toggle mb-4">
                                <span></span>
                                <input
                                  type="checkbox"
                                  value={showDifferenceOnly}
                                  onClick={() => setShowDifferenceOnly(!showDifferenceOnly)}
                                />
                                <span
                                  className="custom-toggle-slider rounded-circle text-success"
                                  data-label-off="No"
                                  data-label-on="Yes"
                                />
                              </Label>
                              <Table striped>
                                <thead>
                                  <tr>
                                    <th>
                                      Field
                                    </th>
                                    <th>
                                      Before
                                    </th>
                                    <th>
                                      After
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {
                                    tableData
                                    .filter(o => {
                                      if(showDifferenceOnly){
                                        return !o.sameValue
                                      }
                                      return true
                                    })
                                    .map(o => {
                                      return (
                                        <tr>
                                          <th scope="row">
                                            {o.fieldName}
                                          </th>
                                          <td>
                                            {o.before}
                                          </td>
                                          <td>
                                            {o.after}
                                          </td>
                                        </tr>
                                      )
                                    })
                                  }
                                </tbody>
                              </Table>
                            </>
                          }
                        </>
                      )
                    })
                  }
                </ListGroup>
              </CardBody>
            </Card>
          </Collapse>
        </CardBody>
      </Card>
    </>
  )
}

FinancePaymentsView.propTypes    = propTypes
FinancePaymentsView.defaultProps = defaultValues

export default withLoader(FinancePaymentsView, CardLoader)